import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle, Send, Trash2, Pencil, Paperclip } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import {
  useJvInvoices,
  useCreateJvInvoice,
  useUpdateJvInvoice,
  useDeleteJvInvoice,
  useSubmitJvInvoice,
  useConsortiumAgreements,
  useSetInvoiceConsortium,
} from "@/features/finances/hooks/useFinances";
import type { JointVentureInvoice } from "@/features/finances/types";
import type { ClientInvoice, JointVentureInvoiceStatus } from "@/features/finances/types";

const STATUS_COLOR: Record<JointVentureInvoiceStatus, "gray" | "blue" | "orange" | "green" | "red" | "yellow"> = {
  draft: "yellow",
  pending: "gray",
  approved: "blue",
  transfer_prepared: "orange",
  rejected: "red",
  paid: "green",
};

interface Props {
  invoice: ClientInvoice;
}

/**
 * "Faktury partnera JV" — partner's invoice(s) attached to OUR PRZ invoice.
 * We issue the full FV; the partner bills us their share. Their part is not our
 * revenue (our_net_share = full net − partner net). Document-flow + payment
 * green-light. Visible/editable only to CEO + consortium-granted users.
 */
export function JointVentureSection({ invoice }: Props) {
  const hasAccess = useAuthStore((s) => s.user?.has_consortium_access === true);
  // Both endpoints 403 anyone without consortium access (CEO + granted users only),
  // so don't fire them at all for everyone else — the section renders null anyway.
  const { data: jvInvoices = [] } = useJvInvoices(hasAccess ? invoice.id : undefined);
  const createJv = useCreateJvInvoice(invoice.id);
  const updateJv = useUpdateJvInvoice(invoice.id);
  const deleteJv = useDeleteJvInvoice(invoice.id);
  const submitJv = useSubmitJvInvoice(invoice.id);
  const setConsortium = useSetInvoiceConsortium(invoice.id);
  const { data: agreements = [] } = useConsortiumAgreements(true, hasAccess);

  // Warning when the share implied by attached partner FVs diverges from the
  // declared % in the agreement.
  const declaredPct = invoice.consortium_agreement_detail
    ? parseFloat(invoice.consortium_agreement_detail.our_share_percent)
    : null;
  const fullNet = parseFloat(invoice.amount_net);
  const actualPct =
    parseFloat(invoice.jv_partner_net) > 0 && fullNet > 0
      ? (parseFloat(invoice.our_net_share) / fullNet) * 100
      : null;
  const shareWarning = declaredPct !== null && actualPct !== null && Math.abs(declaredPct - actualPct) > 0.5;

  const EMPTY = { consortium_agreement: "", partner_name: "", invoice_number: "", amount_net: "", vat_rate: "23", invoice_date: "", notes: "" };
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  // Partner FV file (PDF z KSeF): a freshly picked File replaces/attaches; the
  // detail of an already-attached file lives on the edited JV row.
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [existingFile, setExistingFile] = useState<JointVentureInvoice["managed_file_detail"]>(null);

  // Brutto liczone automatycznie z netto × (1 + VAT) — pole zablokowane.
  const grossComputed = (parseFloat(form.amount_net || "0") * (1 + parseFloat(form.vat_rate || "0") / 100)).toFixed(2);

  // Consortium internals are visible/editable only to CEO + granted users.
  if (!hasAccess) return null;

  function resetFileState() {
    setFile(null);
    setRemoveFile(false);
  }

  function openAdd() {
    setEditingId(null);
    setForm(EMPTY);
    setExistingFile(null);
    resetFileState();
    setShowAdd(true);
  }

  function openEdit(jv: JointVentureInvoice) {
    const net = parseFloat(jv.amount_net);
    const vr = net > 0 ? Math.round((parseFloat(jv.amount_gross) / net - 1) * 100).toString() : "23";
    setEditingId(jv.id);
    setForm({
      consortium_agreement: jv.consortium_agreement ?? "",
      partner_name: jv.partner_name ?? "",
      invoice_number: jv.invoice_number ?? "",
      amount_net: jv.amount_net,
      vat_rate: vr,
      invoice_date: jv.invoice_date ?? "",
      notes: jv.notes ?? "",
    });
    setExistingFile(jv.managed_file_detail);
    resetFileState();
    setShowAdd(true);
  }

  function submit(asStatus: "draft" | "pending" = "pending") {
    const onSuccess = () => {
      setShowAdd(false);
      setEditingId(null);
      setForm(EMPTY);
      setExistingFile(null);
      resetFileState();
    };
    if (editingId) {
      updateJv.mutate(
        {
          id: editingId,
          data: {
            consortium_agreement: form.consortium_agreement || null,
            partner_name: form.partner_name || undefined,
            invoice_number: form.invoice_number || undefined,
            amount_net: form.amount_net,
            amount_gross: grossComputed,
            invoice_date: form.invoice_date || null,
            notes: form.notes || undefined,
            ...(file ? { invoice_file: file } : {}),
            ...(removeFile && !file ? { remove_file: true } : {}),
          },
        },
        { onSuccess },
      );
      return;
    }
    createJv.mutate(
      {
        client_invoice: invoice.id,
        consortium_agreement: form.consortium_agreement || null,
        partner_name: form.partner_name || undefined,
        invoice_number: form.invoice_number || undefined,
        amount_net: form.amount_net,
        amount_gross: grossComputed,
        invoice_date: form.invoice_date || null,
        notes: form.notes || undefined,
        status: asStatus,
        ...(file ? { invoice_file: file } : {}),
      },
      {
        onSuccess,
      },
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Konsorcjum / JV</h3>
        {invoice.is_consortium && <Badge color="purple">Konsorcjum</Badge>}
        {parseFloat(invoice.jv_partner_net) > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Nasza część netto:{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-300">{formatAmount(invoice.our_net_share)} PLN</span>
            {" · "}część partnera: {formatAmount(invoice.jv_partner_net)} PLN
          </span>
        )}
        <button
          onClick={openAdd}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
        >
          <Plus size={13} /> Dodaj FV partnera
        </button>
      </div>

      {/* Tag this PRZ invoice with a consortium agreement (umowa ramowa) */}
      <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
        Umowa konsorcyjna:
        <select
          value={invoice.consortium_agreement ?? ""}
          onChange={(e) => setConsortium.mutate(e.target.value || null)}
          disabled={setConsortium.isPending}
          className="border border-[var(--color-border)] bg-transparent px-2 py-1 text-xs min-w-[200px]"
        >
          <option value="">— brak (nie konsorcyjna) —</option>
          {agreements.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.partner_name})
            </option>
          ))}
        </select>
        {invoice.consortium_agreement_detail && (
          <span className="text-gray-400">nasz udział {invoice.consortium_agreement_detail.our_share_percent}%</span>
        )}
      </label>

      {shareWarning && (
        <div className="mb-3 flex items-start gap-2 text-xs px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Udział z dopiętych FV partnera (<b>{actualPct!.toFixed(1)}%</b> naszego) różni się od deklarowanego w umowie (<b>{declaredPct!.toFixed(1)}%</b>). Sprawdź kwoty.
          </span>
        </div>
      )}

      {jvInvoices.length > 0 && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-2">
          Szkice składasz tutaj. Po złożeniu — akceptacja, płatność i księgowanie w module{" "}
          <Link to="/expenses" className="text-[var(--color-primary)] hover:underline">Faktury kosztowe</Link>.
        </p>
      )}

      {jvInvoices.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">Brak dopiętych faktur partnera.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-widest">Partner</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-widest">Nr FV</th>
                <th className="text-right px-3 py-2 font-mono uppercase tracking-widest">Netto</th>
                <th className="text-right px-3 py-2 font-mono uppercase tracking-widest">Brutto</th>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-widest">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {jvInvoices.map((jv) => (
                <tr key={jv.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {jv.resolved_partner_name}
                    {jv.consortium_agreement_detail &&
                      (jv.consortium_agreement_detail.partner_nip || jv.consortium_agreement_detail.partner_bank_account) && (
                        <span className="block text-[10px] text-gray-400">
                          {jv.consortium_agreement_detail.partner_nip && `NIP ${jv.consortium_agreement_detail.partner_nip}`}
                          {jv.consortium_agreement_detail.partner_bank_account &&
                            ` · ${jv.consortium_agreement_detail.partner_bank_account}`}
                        </span>
                      )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1.5">
                      {jv.invoice_number || "—"}
                      {jv.managed_file_detail &&
                        (jv.managed_file_detail.url ? (
                          <a
                            href={jv.managed_file_detail.url}
                            target="_blank"
                            rel="noreferrer"
                            title={`Plik: ${jv.managed_file_detail.original_filename}`}
                            className="text-[var(--color-primary)] hover:text-[var(--color-primary)]/80"
                          >
                            <Paperclip size={12} />
                          </a>
                        ) : (
                          <Paperclip size={12} className="text-gray-400" aria-label="Plik dołączony" />
                        ))}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">{formatAmount(jv.amount_net)}</td>
                  <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{formatAmount(jv.amount_gross)}</td>
                  <td className="px-3 py-2">
                    <Badge color={STATUS_COLOR[jv.status]}>{jv.status_display}</Badge>
                    {jv.approved_by_name && jv.status !== "pending" && (
                      <span className="ml-1 text-[10px] text-gray-400">({jv.approved_by_name})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {jv.status === "draft" && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(jv)}
                          title="Edytuj szkic"
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-[var(--color-primary)] hover:underline"
                        >
                          <Pencil size={12} /> Edytuj
                        </button>
                        <button
                          onClick={() => submitJv.mutate(jv.id)}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-primary)] hover:underline"
                        >
                          <Send size={12} /> Złóż
                        </button>
                        <button
                          onClick={() => { if (confirm("Usunąć szkic FV partnera?")) deleteJv.mutate(jv.id); }}
                          title="Usuń szkic"
                          className="text-gray-400 hover:text-red-600"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title={editingId ? "Edytuj szkic FV partnera" : "Dodaj FV partnera JV"} size="md">
        <div className="flex flex-col gap-3">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Umowa konsorcyjna (partner)
            <select
              value={form.consortium_agreement}
              onChange={(e) => setForm({ ...form, consortium_agreement: e.target.value })}
              className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
            >
              <option value="">— wpisz partnera ręcznie —</option>
              {agreements.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.partner_name})
                </option>
              ))}
            </select>
          </label>
          {!form.consortium_agreement && (
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Partner
              <input
                value={form.partner_name}
                onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                placeholder="np. itB Legal"
                className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
              />
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Numer FV partnera
              <input
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Data FV
              <DatePicker
                value={form.invoice_date}
                onChange={(v) => setForm({ ...form, invoice_date: v })}
                className="mt-1 border-[var(--color-border)] bg-transparent"
              />
            </label>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Netto (PLN)
              <input
                type="number"
                step="0.01"
                value={form.amount_net}
                onChange={(e) => setForm({ ...form, amount_net: e.target.value })}
                className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              VAT
              <select
                value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
              >
                <option value="23">23%</option>
                <option value="8">8%</option>
                <option value="5">5%</option>
                <option value="0">0%</option>
              </select>
            </label>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 col-span-2">
              Brutto (PLN) — liczone automatycznie
              <input
                type="text"
                value={formatAmount(grossComputed)}
                readOnly
                disabled
                className="mt-1 w-full border border-[var(--color-border)] bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-1.5 text-sm cursor-not-allowed"
              />
            </label>
          </div>

          {/* Plik FV partnera (PDF z KSeF) */}
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Plik faktury partnera (PDF)
            {existingFile && !removeFile && !file && (
              <div className="mt-1 flex items-center gap-2 text-sm">
                <Paperclip size={13} className="shrink-0 text-[var(--color-primary)]" />
                {existingFile.url ? (
                  <a href={existingFile.url} target="_blank" rel="noreferrer" className="truncate text-[var(--color-primary)] hover:underline">
                    {existingFile.original_filename}
                  </a>
                ) : (
                  <span className="truncate text-gray-700 dark:text-gray-300">{existingFile.original_filename}</span>
                )}
                <button
                  type="button"
                  onClick={() => setRemoveFile(true)}
                  title="Odepnij plik"
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            )}
            <FileDropZone
              className="mt-1"
              accept="application/pdf,image/*"
              files={file ? [file] : []}
              onFilesChange={(fs) => { setFile(fs[0] ?? null); setRemoveFile(false); }}
              label={
                existingFile && !removeFile && !file
                  ? "Przeciągnij nowy plik lub kliknij, aby zmienić"
                  : undefined
              }
            />
            {removeFile && !file && (
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                Plik zostanie odpięty po zapisaniu.{" "}
                <button type="button" onClick={() => setRemoveFile(false)} className="underline">Cofnij</button>
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-1">
            <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)}>
              Anuluj
            </Button>
            {editingId ? (
              <Button
                size="sm"
                loading={updateJv.isPending}
                disabled={!form.amount_net || (!form.consortium_agreement && !form.partner_name)}
                onClick={() => submit()}
              >
                Zapisz zmiany
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={createJv.isPending}
                  disabled={!form.amount_net || (!form.consortium_agreement && !form.partner_name)}
                  onClick={() => submit("draft")}
                >
                  Zapisz jako szkic
                </Button>
                <Button
                  size="sm"
                  loading={createJv.isPending}
                  disabled={!form.amount_net || (!form.consortium_agreement && !form.partner_name)}
                  onClick={() => submit("pending")}
                >
                  Dodaj i złóż
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
